/**
 * scanRouter — integration glue (BL capture ⇆ BR shell consumers).
 *
 * BL's `DocumentTileGrid` / `ReviewDropStrip` require an `onScan` extraction
 * dispatch (and accept an `autoClassify`) so the parent routes captured bytes
 * to the right Azure DI model. BR's shell consumers (NRA section, review body,
 * entity shell) don't carry that routing themselves. This module produces a
 * ready-to-pass `onScan` + `autoClassify` pair backed by the canonical
 * `routeCapture` pipeline (`individual/captureRouting`) — the SAME pipeline the
 * deleted UnifiedCaptureSurface used — so the seam is real extraction, not a
 * stub.
 *
 * Flow per capture:
 *   1. `onScan(docTypeValue, base64, mime)` → `routeCapture(...)` which classifies,
 *      routes to azure-di-id | v3-nra | no-extraction, extracts, and (when cart
 *      context is threaded) uploads to ao_Documents.
 *   2. The resulting empty-fields-only `patch` is applied to the draft via the
 *      caller's `applyPatch` (never clobbers officer-typed values — that rule
 *      lives in routeCapture).
 *   3. The extracted values are reduced to BL's `ScanResultPayload.fields`
 *      shape so CaptureModal's review pane renders Detected-as + field rows and
 *      the cross-doc comparator (C4) gets real field text.
 *
 * The AttachedDoc that PERSISTS onto the draft is the one BL's grid builds from
 * the merged-PDF artifact + emits via `onAttachedDocsChange` (Contract C2). The
 * AttachedDoc that `routeCapture` builds is the UPLOAD record (carries fileUrl /
 * spItemId); we don't double-persist it here.
 */
import type { ApiClient } from '@retail-ops/api-client';
import type { RaoPerson } from '../../../types';
import type { ScanResultPayload } from './CaptureModal';
import { autoClassifyCapture } from './autoClassify';
import {
  routeCapture,
  findDocType,
  type RouteCaptureInput,
} from '../individual/captureRouting';

/** Context the router threads into routeCapture for extraction + upload. */
export interface ScanRouterContext {
  apiClient: ApiClient;
  /** The live person draft — drives empty-fields-only patching. */
  person: RaoPerson;
  /** Applies the routeCapture patch to the draft (the caller's onChange). */
  applyPatch: (patch: Partial<RaoPerson>) => void;
  /** Cart id — enables the ao_Documents upload step when present. */
  cartId?: string;
  /** Stable per-person ref (cifId / draft id) — required for upload. */
  personRefId?: string;
  /** Officer's Core Director code — required for upload. */
  officerCode?: string;
  /** Entity type for routing telemetry (Individual / Minor / …). */
  entityType?: string;
}

/**
 * Build the BL-compatible `onScan(docTypeValue, base64, mime)` dispatch. The
 * keying matches both `DocumentTileGrid` (which passes the tile's docTypeValue)
 * and `ReviewDropStrip` (which passes the chosen doc-type). When `docTypeValue`
 * is absent the routing pipeline's classifier resolves it.
 */
export function makeScanHandler(
  ctx: ScanRouterContext,
): (docTypeValue: string, base64: string, mimeType: string) => Promise<ScanResultPayload> {
  return async (docTypeValue, base64, mimeType) => {
    // no-extraction doc types attach without an extraction pass — surface a
    // successful empty-fields payload so the modal still moves to Apply.
    const opt = findDocType(docTypeValue);
    if (opt && opt.mode === 'no-extraction') {
      return { success: true, fields: {}, needsConfirm: [] };
    }
    try {
      const input: RouteCaptureInput = {
        docType: docTypeValue || 'other',
        base64,
        mimeType,
        person: ctx.person,
        apiClient: ctx.apiClient,
        ...(ctx.cartId ? { cartId: ctx.cartId } : {}),
        ...(ctx.personRefId ? { personRefId: ctx.personRefId } : {}),
        ...(ctx.officerCode ? { officerCode: ctx.officerCode } : {}),
        ...(ctx.entityType ? { entityType: ctx.entityType } : {}),
      };
      const result = await routeCapture(input);

      // Cross-contamination fix (Bill 2026-05-28): captureRouting's mappers
      // now write patch.extra with ONLY new-this-scan keys (was pre-spreading
      // person.extra, causing a DL scan's CIP fields to bleed into a
      // subsequent SSN / Green Card scan's review pane). Deep-merge here so
      // every consumer's shallow {...prev, ...patch} onChange preserves
      // existing extras. PdwShell also deep-merges as a belt-and-suspenders;
      // this layer covers IndividualReviewBody / IndividualNraSection /
      // EntityConvergedShell which still use shallow setState.
      if (Object.keys(result.patch).length > 0) {
        const mergedPatch: Partial<RaoPerson> = { ...result.patch };
        if (result.patch.extra) {
          const prevExtra = (ctx.person.extra ?? {}) as Record<string, unknown>;
          const patchExtra = result.patch.extra as Record<string, unknown>;
          const mergedExtra: Record<string, unknown> = { ...prevExtra, ...patchExtra };
          // extracts.* — per-doc-type permanent audit store. Sub-merge so
          // a new doc-type joins existing ones without clobbering.
          if (patchExtra.extracts || prevExtra.extracts) {
            const prevExtracts = (prevExtra.extracts ?? {}) as Record<string, unknown>;
            const patchExtracts = (patchExtra.extracts ?? {}) as Record<string, unknown>;
            mergedExtra.extracts = { ...prevExtracts, ...patchExtracts };
          }
          mergedPatch.extra = mergedExtra;
        }
        ctx.applyPatch(mergedPatch);
      }

      // Reduce the ORIGINAL only-new-keys patch to BL's field-row map so the
      // review pane + the C4 comparator show ONLY values extracted on THIS
      // scan (no bleed-through from prior tile captures).
      const fields = patchToFields(result.patch);
      return {
        success: !result.extractionError,
        fields,
        // BE needsConfirm passthrough (was hardcoded []) — the heuristic
        // address-letter rows (fixed 0.66 confidence) and every other
        // below-floor field flag through to the modal's confirm UX.
        needsConfirm: result.needsConfirm ?? [],
        ...(result.uprightRotationDeg ? { uprightRotationDeg: result.uprightRotationDeg } : {}),
        ...(result.extractionError ? { error: result.extractionError } : {}),
        ...(result.detectedRawType ? { raw: { detectedRawType: result.detectedRawType, mismatchWarning: result.mismatchWarning } } : {}),
        // NRA smoke test (Bill 2026-06-12) — stated-vs-extracted conflicts
        // (e.g. pre-selected citizenship ≠ document) surface in the capture
        // modal as a pick-one alert; the FSR resolves before Apply.
        ...(result.conflicts && result.conflicts.length > 0 ? { conflicts: result.conflicts } : {}),
      };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : String(e) };
    }
  };
}

/**
 * Build the BL-compatible `autoClassify` callback bound to the api client —
 * the salvaged 2-pass classifier (`autoClassifyCapture`). Returned in the
 * shape CaptureModal/DocumentTileGrid expect.
 */
export function makeAutoClassify(
  apiClient: ApiClient,
  context?: { cartId?: string },
): (base64: string, mimeType: string) => Promise<{ docTypeValue: string; detectedRawType?: string }> {
  // Cart linkage (Bill 2026-10-16): the classifier's Azure DI call writes the
  // dbo.DocumentExtract row, so it carries the cart id when the host has one.
  const ctx = context?.cartId ? { cartId: context.cartId } : undefined;
  return (base64, mimeType) => autoClassifyCapture(base64, mimeType, apiClient, ctx);
}

/**
 * Sentinel confidence for a field that has NO real OCR score (manually typed,
 * derived, or applied without a matching per-doc extract). The review pane
 * renders this as "manual" — NEVER a fabricated percentage. (Bill 2026-06-03 —
 * the alarming bug: wrong extractions were shown at 100%.)
 */
export const UNSCORED_CONFIDENCE = -1;

/**
 * Flatten a RaoPerson patch (top-level + `extra`) into the
 * `Record<string,{value,confidence}>` field map BL's pipeline carries.
 *
 * Bill 2026-06-03 — CRITICAL confidence-integrity fix. The old code stamped
 * every applied field `confidence: 1` and let that fabricated 100% WIN over the
 * real per-field Azure DI score (which lives in `extra.extracts.<docType>`).
 * Result: a garbage extraction (e.g. a bad-quality DL whose address mis-read)
 * was shown to the FSR at 100% — inverting the one signal they use to know what
 * to scrutinise. We now take the VALUE from the applied patch but the
 * CONFIDENCE from the real extract (real score wins on collision); a field with
 * no extract score is marked UNSCORED, never 100%.
 */
function patchToFields(
  patch: Partial<RaoPerson>,
): Record<string, { value: string | null; confidence: number }> {
  const out: Record<string, { value: string | null; confidence: number }> = {};
  const extra = (patch.extra ?? {}) as Record<string, unknown>;

  // extracts use the NraScanFields spelling (dateOfBirth/sex/documentNumber)
  // while the top-level patch uses RaoPerson keys (dob/gender) — alias the
  // colliding ones (matches toConflictDoc's g('dateOfBirth','dob') aliasing).
  const EXTRACT_KEY_ALIAS: Record<string, string> = {
    dateOfBirth: 'dob',
    sex: 'gender',
    idTypeCode: 'idType',
  };

  // 1. REAL per-field confidence is the source of truth — build it from the
  //    per-doc Azure DI audit store FIRST, and surface every read leaf so the
  //    review pane shows the document's actual extraction (GAP-A1/A2 — even
  //    fields the empty-only guard kept off the person still render here).
  const realConf: Record<string, number> = {};
  const extracts = (extra.extracts ?? {}) as Record<string, unknown>;
  for (const docFields of Object.values(extracts)) {
    if (!docFields || typeof docFields !== 'object') continue;
    for (const [rawKey, fv] of Object.entries(docFields as Record<string, unknown>)) {
      const fk = EXTRACT_KEY_ALIAS[rawKey] ?? rawKey;
      if (fv && typeof fv === 'object' && 'value' in fv) {
        const val = (fv as { value: unknown }).value;
        if (val != null && val !== '') {
          const c = Number((fv as { confidence?: number }).confidence ?? 0);
          if (!(fk in realConf)) realConf[fk] = c;
          if (!(fk in out)) out[fk] = { value: String(val), confidence: c };
        }
      }
    }
  }

  // 2. Applied person-patch values overlay the VALUE (post-normalisation), but
  //    keep the REAL confidence from the extract — NEVER a fabricated 1. A field
  //    with no extract score is UNSCORED (manual/derived), shown as "manual".
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'extra') continue;
    if (typeof v === 'string' && v !== '') {
      out[k] = { value: v, confidence: realConf[k] ?? UNSCORED_CONFIDENCE };
    }
  }
  for (const [k, v] of Object.entries(extra)) {
    if (k === 'extracts') continue;
    if (typeof v === 'string' && v !== '') {
      out[k] = { value: v, confidence: realConf[k] ?? UNSCORED_CONFIDENCE };
    }
  }

  return out;
}
